import { type QueryUserKindeProperties } from '@/context/kinde-properties-context';
import {
  authSessionMetadataAtom,
  getAccessToken,
  type AuthSessionMetadata,
} from '@/lib/auth';
import { getEffectiveAuthProvider } from '@/lib/auth/effectiveAuthProvider';
import { isRegionalRoutingEnabled } from '@/lib/region-routing';
import { isTauri } from '@/tauri/utils';
import { AuthProvider } from '@/utils/auth/persistedAuthProvider';
import { logger } from '@/utils/logger/logger';
import {
  extractRegionFromAccessToken,
  extractUserRefIdFromAuth0Token,
} from '@heidi/auth/tokens';
import { toast } from '@heidi/design-system/toast';
import { type Region } from '@heidi/schema/ml-scribe';
import { updateUserRegion } from '@heidi/workspace/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getDefaultStore } from 'jotai';
import { GET_KINDE_PROPERTIES } from '../query/useQueryKindeProperties';

/**
 * Kinde-web direct-refresh: the cache's Kinde-web branch is a stub
 * that returns null (waiting on `add-kinde-session-invalidation`,
 * ticket MUL-2616), so routing region refresh through
 * `getAccessToken({forceRefresh:true})` would produce a bare-null
 * outcome — `authSessionMetadataAtom` stays null (no region to
 * redirect on) AND `authStateAtom` flips to `'unauthenticated'` as
 * a side effect. Hit the endpoint directly here so the caller gets
 * the new region AND the cache atoms are left untouched. Remove
 * once the cache's Kinde-web branch is implemented.
 *
 * Not a rotation-race concern: the cache never fetches Kinde-web
 * (stub), so no second refresher can compete with this call.
 */
async function refreshKindeWebSession(): Promise<AuthSessionMetadata | undefined> {
  try {
    const response = await fetch('/api/kinde-session?refresh=true', {
      credentials: 'include',
      cache: 'no-store',
    });
    if (!response.ok) return undefined;
    const body = (await response.json()) as AuthSessionMetadata | null;
    return body ?? undefined;
  } catch {
    // Swallow fetch / json-parse rejections and resolve `undefined`
    // — legacy parity with the deleted `refresh-region-session`
    // helper. `updateUserRegion` has already committed server-side
    // by this point; a network drop on the session refresh must
    // NOT fail the whole mutation, or callers like
    // `useOrganisationSurvey.onSubmit` — which awaits this INSIDE
    // the try wrapping the entire onboarding save — abandon name /
    // specialty / org / signup analytics writes. The follow-up
    // region redirect self-heals on the next refresh cycle.
    return undefined;
  }
}

/**
 * Force a desktop token rotation once the region has been committed server-side.
 *
 * Desktop has no `/api/*-session` endpoint, so unlike web there is nothing for
 * the CALLER to read back here — but the rotation is still required, for a
 * reason that has nothing to do with the caller's redirect decision.
 *
 * The native auth store decodes `user_properties.region` out of every freshly
 * minted access token and writes it to the OS keychain as the cached identity
 * (`tauri-plugin-auth0` — `store.rs` `persist_session`, via
 * `secure_storage.rs` `CachedIdentity::from_access_token`, which returns `None`
 * unless BOTH `user_ref_id` and `region` are present). That region is the ONLY
 * input that resolves the connectivity probe target: the probe's sole
 * `set_target` path reads it and otherwise clears the target
 * (`src-tauri/src/offline_recording/commands.rs` —
 * `offline_connectivity_refresh_target` / `resolve_probe_target`). So a desktop
 * client holding a region-less token has no cached identity, no probe target,
 * and a connectivity state pinned at `unknown` for the whole process — which
 * silently disables every offline surface and, because the offline flag cache
 * is only written from a confirmed-online reading
 * (`openfeature-context-sync.tsx` gates its write on `isDesktopOnlineReady`),
 * leaves the next cold launch in the same state.
 *
 * That is exactly a first-time user: their token is minted before the country
 * picker assigns a region, and nothing else re-mints it inside the visit — the
 * connectivity sync hook re-arms only on its own mount, on an auth/flag change,
 * or on the plugin's `auth:session-refreshed` event, and it is mounted in `_app`
 * so the post-onboarding navigation does not remount it
 * (`useOfflineConnectivitySync.ts`). Rotating here is what makes the region
 * reach the keychain in the same visit rather than at the next sign-in — the
 * reason signing out and back in "fixed" it.
 *
 * Awaited for its COMPLETION, not for its value — do not make this
 * fire-and-forget. The mutation must not resolve until the rotation has either
 * landed or failed, because the caller navigates straight out of onboarding on
 * resolve and the keychain write has to have happened by then.
 *
 * Failure is best-effort and never propagates. `useOrganisationSurvey.onSubmit`
 * awaits this mutation inside the try that wraps the whole onboarding save, so
 * a thrown rotation failure would cost the user their name, specialty and
 * organisation writes — over something a later rotation recovers on its own.
 * Same rule the Kinde-web helper above follows, for the same caller. What
 * "later" is worth is stated once, at the call site below.
 *
 * Note which failure signal is the live one: the token cache's `launchFetch`
 * wraps every provider branch in its own catch and settles through `settle(null,
 * …)` on each exit (`src/lib/auth/getAccessToken.ts`), so `getAccessToken`
 * resolves `null` rather than rejecting and `null` is what a failed rotation
 * actually looks like — it has to be READ, not merely awaited. The catch below
 * is deliberately defensive rather than dead: it exists so a future change to
 * that contract cannot reach the onboarding save.
 *
 * `error.name` and nothing more: this path crosses the desktop auth bridge,
 * whose rejections can carry the account email or IdP subject, and the Datadog
 * sink applies no redaction to free text.
 *
 * A non-null token does NOT prove the identity was written, so the claims are
 * read rather than the nullness alone. `CachedIdentity::from_access_token`
 * requires BOTH `user_ref_id` and `region` to be present and non-empty
 * (`tauri-plugin-auth0/src/secure_storage.rs`), and the predicate below mirrors
 * that exactly — the two extractors read the same `user_properties.<claim>.v`
 * shape the Rust decoder reads. Two distinct misses hide behind a successful
 * rotation otherwise: the coalescing race at the call site can satisfy this
 * call with a pre-PATCH (region-less) token, and `useOrganisationSurvey`
 * performs its get-or-create-user step AFTER this mutation, so a signup path
 * that arrives here before the user record exists rotates a token with no
 * `user_ref_id`. Both leave the probe unarmed while every signal here says
 * success.
 *
 * Diagnostic only — deliberately no retry. The recovery paragraph at the call
 * site applies unchanged; this makes the residual countable, it does not close
 * it. Every value logged is a closed literal, so nothing user-identifying
 * reaches the sink.
 */
async function refreshDesktopSessionForCachedIdentity(): Promise<void> {
  try {
    const token = await getAccessToken({ forceRefresh: true });
    if (token === null) {
      // Message string + trailing object, never a message object: a message
      // object's keys reach Datadog serialised into the message text and are
      // not facetable (`packages/runtime/src/logger/README.md`). All three
      // outcomes below share one message so they are countable under one query.
      logger.warn(
        'Desktop session rotation after a region update did not arm the probe',
        {
          reason: 'null-token',
        }
      );
      return;
    }
    const hasRegion = (extractRegionFromAccessToken(token) ?? '') !== '';
    const hasUserRefId = (extractUserRefIdFromAuth0Token(token) ?? '') !== '';
    if (hasRegion && hasUserRefId) return;
    logger.warn('Desktop session rotation after a region update did not arm the probe', {
      reason: hasRegion ? 'missing-user-ref-id' : 'missing-region',
    });
  } catch (error) {
    logger.warn('Failed to refresh the desktop session after a region update', {
      errorName: error instanceof Error ? error.name : 'UnknownError',
    });
  }
}

export function useMutateUpdateUserRegion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      region: Region
    ): Promise<AuthSessionMetadata | null | undefined> => {
      await updateUserRegion(region);
      // Desktop: rotate the token so the new region reaches the keychain cached
      // identity, then resolve `undefined` — there is still no session payload
      // for the caller to redirect on. See the helper's docstring for why the
      // rotation is not optional on desktop even though its value is unused.
      //
      // KNOWN RESIDUAL — the same race the web branch concedes below, and wider
      // than "two forced callers collide". In `getAccessToken.ts` a forced
      // caller joins an in-flight FORCED fetch outright; separately, a forced
      // caller arriving while a NON-forced fetch is in flight accepts that
      // fetch's token whenever it differs from `options.priorToken`, which this
      // call never sets. So an ordinary near-expiry background refresh can
      // satisfy this rotation with a token minted before the PATCH committed,
      // and the keychain identity is not written on this pass. The helper logs
      // that case rather than retrying it.
      //
      // Recovery is LIKELY but UNBOUNDED, and not what it looks like. Nothing
      // fires on token expiry — there is no timer — and the focus/online
      // handlers (`useAuthTokenCacheScheduler.tsx`) make NON-forced reads, which
      // return a still-valid cached token and mint nothing. What recovers it is
      // the next event that actually MINTS a token: a read finding the cache
      // near expiry, a forced refresh from the 401 retry path, or a fresh
      // sign-in. Each is an event, not a deadline.
      //
      // Closing it needs a commit-fence in the shared token cache, which the
      // 401-retry path and the refresh scheduler also consume — a wider blast
      // radius than this fix carries. The adjudication history behind that
      // call lives in the PR description and the ticket, not here.
      if (isTauri()) {
        await refreshDesktopSessionForCachedIdentity();
        return undefined;
      }
      // Deployments with regional routing disabled never redirect on region, so
      // there is nothing for the caller to read from a forced refresh.
      if (!isRegionalRoutingEnabled()) return undefined;

      // Kinde-web carve-out: the cache's Kinde-web branch is a null
      // stub, so hit /api/kinde-session?refresh=true directly and
      // skip `getAccessToken` entirely (preserves the region read AND
      // leaves the cache atoms alone — a bare-null cache path would
      // flip authStateAtom to 'unauthenticated'). Gate on the effective
      // provider so mid-migration users (cookie=AUTH0, Redux=KINDE)
      // route through the cache instead. See docstring on
      // `refreshKindeWebSession` above; remove once MUL-2616 lands.
      if (getEffectiveAuthProvider() === AuthProvider.KINDE) {
        return refreshKindeWebSession();
      }

      // Route through the cache so a single rotation path owns
      // /api/auth0-session?refresh=true. The cache writes the new session
      // (including the refreshed `region` claim) into authSessionMetadataAtom,
      // which the caller reads to decide whether to redirect.
      //
      // On a transient failure the cache DELIBERATELY preserves the
      // OLD session in the atoms — reading `authSessionMetadataAtom`
      // unconditionally would hand back the pre-update region with no
      // error signal (correctness by coincidence: stale region matches
      // the current host so no redirect fires, matching the legacy
      // `refreshAccessToken → undefined` behaviour by accident, not by
      // design). Return `undefined` on a null result instead so the
      // caller's redirect decision is made against explicit
      // "no fresh session" input.
      //
      // Accepted trade-off (no fix): a forced caller joining an
      // in-flight forced fetch that started BEFORE this
      // `updateUserRegion` server call committed can read a
      // pre-update region claim. Rare, benign (redirect skipped,
      // self-heals on the next refresh); a join-fence on the cache
      // would be out of proportion for the failure mode.
      const token = await getAccessToken({ forceRefresh: true });
      if (token === null) return undefined;
      return getDefaultStore().get(authSessionMetadataAtom);
    },
    onMutate: async (region) => {
      await queryClient.cancelQueries({
        queryKey: [GET_KINDE_PROPERTIES],
      });
      const previousRegion = queryClient.getQueryData<QueryUserKindeProperties>([
        GET_KINDE_PROPERTIES,
      ]);
      queryClient.setQueryData<QueryUserKindeProperties>([GET_KINDE_PROPERTIES], () => {
        return {
          region: region,
        };
      });
      return { previousRegion };
    },
    onError: (error, _region, context) => {
      if (context?.previousRegion !== undefined) {
        queryClient.setQueryData<QueryUserKindeProperties>(
          [GET_KINDE_PROPERTIES],
          context.previousRegion
        );
      } else {
        queryClient.removeQueries({
          queryKey: [GET_KINDE_PROPERTIES],
          exact: true,
        });
      }
      logger.error('Error updating user region:', error);
      toast({
        tone: 'destructive',
        title: 'Error updating user region',
        // description: error.errors[0]?.message,
      });
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({
        queryKey: [GET_KINDE_PROPERTIES],
      });
    },
  });
}
